import React, { useState, useCallback, useEffect, useMemo } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import {
  Pencil,
  Check,
  LayoutTemplate,
  Loader,
  Upload,
  AlertTriangle,
  ExternalLink,
  Phone,
  MessageSquare,
} from "lucide-react";
import { getTemplates } from "../../apis/template";
import { getFlowAllocatedPhones } from "../../apis/flowAPI";
import { uploadDocumentFile } from "../../apis/documents";
import SearchableSelect from "../ui/SearchableSelect";
import TemplatePreview from "../templateCreator/TemplatePreview";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";
import { buildTemplatePreviewState } from "../../utils/campaignTemplatePreview";
import { buildUserVarsFromFormValues } from "../../utils/campaignTemplateUtils";
import {
  applyTemplateSelection,
  hasQuickReplyButtons,
  resolveMappingValues,
} from "../../utils/preApprovedTemplateNodeUtils";
import { usePhoneAccount } from "../../contexts/PhoneAccountContext";

const filterApprovedTemplates = (rawTemplates) => {
  const approved = rawTemplates.filter(
    (t) => t.status === "approved" || t.status === "APPROVED"
  );
  const notAuth = approved.filter(
    (t) => (t.category || "").toLowerCase() !== "authentication"
  );
  const withQuality = notAuth.filter((t) => {
    const q = (t.quality_score || "").toString().toUpperCase();
    return q !== "RED";
  });
  return withQuality.length > 0 ? withQuality : notAuth;
};

const CollapsedView = ({ data, onEdit }) => {
  const templateName = data.templateName || "Select template";
  const quickButtons = (data.buttons || []).filter((b) => b.type === "QUICK_REPLY");
  const mappingCount = (data.variableMappings || []).length;

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#E8F4FD", padding: "10px 10px 6px", minWidth: "260px", maxWidth: "300px" }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all"
        title="Edit"
      >
        <Pencil size={12} className="text-blue-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm relative">
        <div className="absolute -left-[6px] top-0 w-3 h-3 overflow-hidden">
          <div className="absolute bg-white w-3 h-3 transform rotate-45 translate-x-[6px] shadow-sm" />
        </div>
        <div className="px-3 py-2.5">
          <div className="flex items-center gap-1.5 mb-1.5">
            <LayoutTemplate size={13} className="text-blue-600" />
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wide">Pre-approved</span>
          </div>
          <p className="text-[13px] font-semibold text-[#111B21] truncate">{templateName}</p>
          {data.templateLanguage && (
            <p className="text-[10px] text-[#667781] mt-0.5">{data.templateLanguage}</p>
          )}
          {mappingCount > 0 && (
            <p className="text-[10px] text-[#667781] mt-1">{mappingCount} variable(s) mapped</p>
          )}
          {data.headerMediaOverride && (
            <p className="text-[10px] text-blue-600 mt-0.5 truncate">Custom header media</p>
          )}
        </div>

        {quickButtons.length > 0 && (
          <div className="border-t border-gray-100">
            {quickButtons.map((btn, i) => (
              <div
                key={btn.id}
                className={`flex items-center justify-center gap-1.5 py-[7px] ${i < quickButtons.length - 1 ? "border-b border-gray-100" : ""}`}
              >
                <MessageSquare size={11} className="text-[#027EB5]" />
                <span className="text-[11px] font-medium text-[#027EB5]">{btn.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {quickButtons.length > 0 && (
        <div className="absolute -right-16 top-0 h-full flex flex-col justify-around items-start py-4 pointer-events-none">
          {quickButtons.map((btn) => (
            <span key={btn.id} className="text-[7px] text-[#667781] font-semibold truncate max-w-[60px] text-center">
              {btn.text}
            </span>
          ))}
          <span className="text-[7px] text-[#F59E0B] font-semibold truncate max-w-[60px] text-center">Other</span>
        </div>
      )}
    </div>
  );
};

const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const { phoneAccounts, fetchPhoneAccounts, loading: phonesLoading } = usePhoneAccount();
  const flowId = data.flowId || null;
  const [allocatedPhones, setAllocatedPhones] = useState([]);
  const [form, setForm] = useState(() => ({
    phoneId: data.phoneId ? String(data.phoneId) : null,
    templateId: data.templateId || null,
    templateName: data.templateName || "",
    templateLanguage: data.templateLanguage || "en_US",
    parameterFormat: data.parameterFormat || "numbered",
    templateSnapshot: data.templateSnapshot || null,
    variableMappings: data.variableMappings ? data.variableMappings.map((m) => ({ ...m })) : [],
    buttons: data.buttons ? data.buttons.map((b) => ({ ...b })) : [],
    headerMediaFormat: data.headerMediaFormat || "NONE",
    headerMediaOverride: data.headerMediaOverride || "",
    selectionVariable: data.selectionVariable || "",
  }));
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchPhoneAccounts("templates");
  }, [fetchPhoneAccounts]);

  useEffect(() => {
    if (!flowId) {
      setAllocatedPhones([]);
      return;
    }
    let cancelled = false;
    getFlowAllocatedPhones(flowId)
      .then((res) => {
        if (!cancelled && res.data?.success) {
          setAllocatedPhones(res.data.phones || []);
        }
      })
      .catch(() => {
        if (!cancelled) setAllocatedPhones([]);
      });
    return () => {
      cancelled = true;
    };
  }, [flowId]);

  const phoneOptions = useMemo(() => {
    if (allocatedPhones.length > 0) {
      return allocatedPhones.map((p) => ({
        value: String(p.phoneId),
        label: p.verifiedName
          ? `${p.verifiedName} • ${p.phoneNumber || p.phoneId}`
          : p.phoneNumber || String(p.phoneId),
        searchText: `${p.phoneNumber || ""} ${p.verifiedName || ""} ${p.phoneId}`.toLowerCase(),
      }));
    }
    return phoneAccounts.map((a) => ({
      value: String(a.phoneId),
      label: `${a.phoneName || a.wabaName || "WhatsApp"} • ${a.phoneNumber || a.phoneId}`,
      searchText: `${a.phoneNumber || ""} ${a.phoneName || ""} ${a.wabaName || ""}`.toLowerCase(),
    }));
  }, [allocatedPhones, phoneAccounts]);

  const activePhoneId = form.phoneId || phoneOptions[0]?.value || null;

  const selectedPhoneOption = useMemo(
    () => phoneOptions.find((o) => o.value === String(activePhoneId)) || null,
    [phoneOptions, activePhoneId]
  );

  useEffect(() => {
    if (!form.phoneId && phoneOptions.length > 0) {
      setForm((prev) => ({ ...prev, phoneId: phoneOptions[0].value }));
    }
  }, [phoneOptions, form.phoneId]);

  const resolveTemplateMatch = (list, { templateId, templateName, templateLanguage }) => {
    if (templateId) {
      const byId = list.find((t) => String(t.id) === String(templateId));
      if (byId) return byId;
    }
    if (!templateName) return null;
    const langBase = String(templateLanguage || "en").toLowerCase().split(/[-_]/)[0];
    return (
      list.find(
        (t) =>
          t.name === templateName &&
          String(t.language || "en").toLowerCase().split(/[-_]/)[0] === langBase
      ) ||
      list.find((t) => t.name === templateName) ||
      null
    );
  };

  const selectedTemplate = useMemo(
    () => resolveTemplateMatch(templates, form),
    [templates, form.templateId, form.templateName, form.templateLanguage]
  );

  useEffect(() => {
    if (form.templateId || !selectedTemplate) return;
    setForm((prev) => ({
      ...prev,
      templateId: selectedTemplate.id,
      templateName: selectedTemplate.name,
      templateLanguage: selectedTemplate.language || prev.templateLanguage,
    }));
  }, [form.templateId, selectedTemplate]);

  const templateOptions = useMemo(
    () =>
      templates.map((t) => ({
        value: String(t.id),
        label: t.name,
        searchText: `${t.name} ${t.language || ""} ${t.category || ""}`.toLowerCase(),
      })),
    [templates]
  );

  const selectedTemplateOption = useMemo(
    () =>
      templateOptions.find((o) => o.value === String(form.templateId)) ||
      (selectedTemplate
        ? templateOptions.find((o) => o.value === String(selectedTemplate.id))
        : null) ||
      null,
    [templateOptions, form.templateId, selectedTemplate]
  );

  const previewValues = useMemo(() => {
    if (!selectedTemplate) return {};
    const resolved = resolveMappingValues(form.variableMappings, flowVariables);
    return buildUserVarsFromFormValues(selectedTemplate, resolved);
  }, [selectedTemplate, form.variableMappings, flowVariables]);

  const previewState = useMemo(() => {
    if (!selectedTemplate) return null;
    const extra = {};
    if (form.headerMediaOverride) {
      const fmt = form.headerMediaFormat;
      if (fmt === "IMAGE") extra.header_image_link = form.headerMediaOverride;
      else if (fmt === "VIDEO") extra.header_video_link = form.headerMediaOverride;
      else if (fmt === "DOCUMENT") extra.header_document_link = form.headerMediaOverride;
    }
    return buildTemplatePreviewState(selectedTemplate, { ...previewValues, ...extra });
  }, [selectedTemplate, previewValues, form.headerMediaOverride, form.headerMediaFormat]);

  useEffect(() => {
    if (!activePhoneId) {
      setTemplates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      setTemplateLoadError(null);
      try {
        const response = await getTemplates({
          limit: 1000,
          offset: 0,
          phoneId: activePhoneId,
          status: "approved",
        });
        const raw = response.data.metaTemplates || response.data.templates || [];
        if (!cancelled) setTemplates(filterApprovedTemplates(raw));
      } catch (err) {
        if (!cancelled) {
          setTemplates([]);
          setTemplateLoadError(
            err.response?.data?.message || err.message || "Failed to load templates"
          );
        }
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activePhoneId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateMapping = (key, flowVariable) => {
    setForm((prev) => ({
      ...prev,
      variableMappings: prev.variableMappings.map((m) =>
        m.key === key ? { ...m, flowVariable } : m
      ),
    }));
  };

  const handlePhoneSelect = (option) => {
    const phoneId = option?.value || null;
    setForm((prev) => ({
      ...prev,
      phoneId,
      templateId: null,
      templateName: "",
      templateLanguage: "en_US",
      templateSnapshot: null,
      variableMappings: [],
      buttons: [],
      headerMediaFormat: "NONE",
      headerMediaOverride: "",
    }));
  };

  const handleTemplateSelect = (option) => {
    if (!option?.value) {
      setForm((prev) => ({
        ...prev,
        templateId: null,
        templateName: "",
        templateLanguage: "en_US",
        templateSnapshot: null,
        variableMappings: [],
        buttons: [],
        headerMediaFormat: "NONE",
        headerMediaOverride: "",
      }));
      return;
    }
    const tpl = templates.find((t) => String(t.id) === String(option.value));
    if (!tpl) return;
    setForm((prev) => ({ ...prev, ...applyTemplateSelection(tpl) }));
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const response = await uploadDocumentFile(file);
      updateField("headerMediaOverride", response.data.data.doc_url);
    } catch {
      /* ignore */
    } finally {
      setUploading(false);
    }
  };

  const buildSavePayload = () => ({
    ...form,
    phoneId: form.phoneId || activePhoneId || null,
  });

  const quickButtons = form.buttons.filter((b) => b.type === "QUICK_REPLY");
  const nonRouteButtons = (form.templateSnapshot?.components || [])
    .find((c) => c.type === "BUTTONS")?.buttons?.filter((b) => b.type !== "QUICK_REPLY") || [];

  return (
    <div
      className="nowheel flex flex-col lg:flex-row gap-4"
      style={{ minWidth: "720px", maxWidth: "960px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex-1 min-w-0">
        <NodeEditorTabs nodeType="preApprovedTemplate">
          <div className="space-y-3 mb-3">
            <div>
              <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1 block">
                WhatsApp phone number
              </label>
              {phonesLoading && phoneOptions.length === 0 ? (
                <div className="flex items-center gap-2 text-[11px] text-gray-500 py-2">
                  <Loader size={14} className="animate-spin" /> Loading phone numbers...
                </div>
              ) : phoneOptions.length === 0 ? (
                <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2.5 py-2">
                  No WhatsApp phone numbers found. Connect a number or allocate this flow to a phone in Flow List.
                </p>
              ) : (
                <>
                  <SearchableSelect
                    options={phoneOptions}
                    value={selectedPhoneOption}
                    onChange={handlePhoneSelect}
                    placeholder="Select phone number..."
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    {allocatedPhones.length > 0
                      ? "Showing phones allocated to this flow."
                      : "Showing all connected numbers — allocate this flow to a phone for a shorter list."}
                  </p>
                </>
              )}
            </div>

            <div>
              <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1 block">
                Approved template
              </label>
              {!activePhoneId ? (
                <p className="text-[11px] text-gray-500 py-2">Select a phone number first.</p>
              ) : loadingTemplates ? (
                <div className="flex items-center gap-2 text-[11px] text-gray-500 py-2">
                  <Loader size={14} className="animate-spin" /> Loading templates...
                </div>
              ) : templateLoadError ? (
                <p className="text-[11px] text-red-600 bg-red-50 rounded-lg px-2.5 py-2">{templateLoadError}</p>
              ) : (
                <SearchableSelect
                  options={templateOptions}
                  value={selectedTemplateOption}
                  onChange={handleTemplateSelect}
                  placeholder={templates.length ? "Search templates..." : "No approved templates for this number"}
                  isClearable
                  variant="campaign"
                  formatOptionLabel={(opt) => {
                    const tpl = templates.find((t) => String(t.id) === String(opt.value));
                    if (!tpl) return opt.label;
                    const meta = [tpl.language, tpl.category].filter(Boolean).join(" · ");
                    return (
                      <div>
                        <div className="font-semibold text-sm">{opt.label}</div>
                        {meta && <div className="text-[11px] text-gray-500">{meta}</div>}
                      </div>
                    );
                  }}
                />
              )}
              {form.templateId && (
                <p className="text-[10px] text-gray-500 mt-1">
                  ID: {form.templateId} · {form.templateLanguage}
                </p>
              )}
              {activePhoneId && !loadingTemplates && templates.length === 0 && !templateLoadError && (
                <p className="text-[10px] text-amber-700 mt-1">
                  No approved templates for this phone. Sync templates in Template Creator.
                </p>
              )}
            </div>

            {form.variableMappings.length > 0 && (
              <div>
                <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1.5 block">
                  Template variables → flow variables
                </label>
                <div className="bg-white rounded-lg border border-gray-100 divide-y divide-gray-100">
                  {form.variableMappings.map((mapping) => (
                    <div key={mapping.key} className="p-2.5">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[11px] font-semibold text-[#111B21]">{mapping.label}</span>
                        <span className="text-[9px] text-gray-400 uppercase">{mapping.component}</span>
                      </div>
                      {mapping.component === "BUTTON" && mapping.urlTemplate && (
                        <p className="text-[9px] text-blue-600 mb-1.5 font-mono truncate" title={mapping.urlTemplate}>
                          URL: {mapping.urlTemplate}
                        </p>
                      )}
                      {mapping.description && mapping.component !== "BUTTON" && (
                        <p className="text-[9px] text-gray-400 mb-1">{mapping.description}</p>
                      )}
                      <VariableDropdown
                        value={(mapping.flowVariable || "").replace(/^\{\{|\}\}$/g, "")}
                        onChange={(name) => updateMapping(mapping.key, name ? `{{${name}}}` : "")}
                        flowVariables={flowVariables || []}
                        allowCreate
                        onCreateVariable={onCreateVariable}
                        placeholder={
                          mapping.component === "BUTTON"
                            ? `Flow variable for ${mapping.placeholder || "URL segment"}`
                            : `Map ${mapping.placeholder || mapping.key}...`
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {["IMAGE", "VIDEO", "DOCUMENT"].includes(form.headerMediaFormat) && (
              <div>
                <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1 block">
                  Header {form.headerMediaFormat.toLowerCase()} (optional override)
                </label>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <input
                    type="text"
                    value={form.headerMediaOverride}
                    onChange={(e) => updateField("headerMediaOverride", e.target.value)}
                    placeholder="https://... or {{variable}}"
                    className="flex-1 px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <VariableDropdown
                    value=""
                    onChange={(name) => {
                      if (name) updateField("headerMediaOverride", `{{${name}}}`);
                    }}
                    flowVariables={flowVariables || []}
                    allowCreate
                    onCreateVariable={onCreateVariable}
                    placeholder="Var"
                    className="w-20"
                  />
                </div>
                <label className="block cursor-pointer">
                  <div className={`rounded-lg p-2 text-center ${uploading ? "bg-blue-50" : "bg-[#F0F0F0] hover:bg-[#E8E8E8]"}`}>
                    {uploading ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader size={14} className="text-blue-600 animate-spin" />
                        <span className="text-[10px] text-blue-600 font-semibold">Uploading...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1.5">
                        <Upload size={14} className="text-[#667781]" />
                        <span className="text-[10px] font-medium text-[#667781]">Upload {form.headerMediaFormat.toLowerCase()}</span>
                      </div>
                    )}
                  </div>
                  <input type="file" className="hidden" accept="image/*,video/*,.pdf" onChange={handleMediaUpload} disabled={uploading} />
                </label>
              </div>
            )}

            {quickButtons.length > 0 && (
              <div>
                <label className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-1 block">
                  Store button selection in variable
                </label>
                <VariableDropdown
                  value={form.selectionVariable || ""}
                  onChange={(name) => updateField("selectionVariable", name)}
                  flowVariables={flowVariables || []}
                  allowCreate
                  onCreateVariable={onCreateVariable}
                  placeholder="Optional: save user's button click..."
                />
                <p className="text-[9px] text-gray-500 mt-1">
                  Quick-reply buttons create routes on this node. URL / phone buttons do not wait for a reply.
                </p>
              </div>
            )}

            {nonRouteButtons.length > 0 && (
              <div className="px-2.5 py-2 bg-gray-50 rounded-lg">
                <p className="text-[10px] font-semibold text-gray-600 mb-1">Non-blocking buttons</p>
                {nonRouteButtons.map((btn, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[10px] text-gray-500">
                    {btn.type === "URL" && <ExternalLink size={10} />}
                    {btn.type === "PHONE_NUMBER" && <Phone size={10} />}
                    <span>{btn.text || btn.type}</span>
                    <span className="text-gray-400">— no wait</span>
                  </div>
                ))}
              </div>
            )}

            {form.templateId && form.variableMappings.some((m) => !m.flowVariable) && (
              <div className="flex items-start gap-1 px-2 py-1.5 bg-amber-50 rounded-lg text-[10px] text-amber-700">
                <AlertTriangle size={11} className="flex-shrink-0 mt-0.5" />
                <span>Some template variables are not mapped to flow variables yet.</span>
              </div>
            )}
          </div>
        </NodeEditorTabs>

        <div className="flex gap-2">
          <button
            onClick={() => onSave(buildSavePayload())}
            className="flex-1 py-2 text-[12px] font-bold bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-1.5"
          >
            <Check size={14} /> Save
          </button>
          <button
            onClick={onCancel}
            className="flex-1 py-2 text-[12px] font-bold bg-white text-[#667781] rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {previewState && (
        <div className="flex-shrink-0 w-[280px]">
          <p className="text-[10px] font-bold text-[#667781] uppercase tracking-wider mb-2">Preview</p>
          <TemplatePreview state={previewState} />
        </div>
      )}
    </div>
  );
};

const PreApprovedTemplateNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("preApprovedTemplate");
  const quickButtons = (data.buttons || []).filter((b) => b.type === "QUICK_REPLY");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]
  );

  return (
    <div className="relative transition-all shadow-md" style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-blue-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={closeEditor}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
        />
      </FlowNodeEditorShell>

      {hasQuickReplyButtons(data.buttons) ? (
        <>
          {quickButtons.map((btn, index) => {
            const pct = ((index + 1) / (quickButtons.length + 2)) * 100;
            return (
              <Handle
                key={btn.id}
                type="source"
                position={Position.Right}
                id={btn.id}
                style={{ top: `${pct}%`, right: -7 }}
                className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-blue-500"
                title={btn.text}
              />
            );
          })}
          <Handle
            key="other"
            type="source"
            position={Position.Right}
            id="other"
            style={{ top: `${((quickButtons.length + 1) / (quickButtons.length + 2)) * 100}%`, right: -7 }}
            className="!w-3 !h-3 !border-2 !border-white !shadow-sm !bg-[#F59E0B]"
            title="Other"
          />
        </>
      ) : (
        <Handle
          type="source"
          position={Position.Right}
          id="default"
          className="!w-3.5 !h-3.5 !bg-blue-500 !border-2 !border-white !shadow-sm"
          style={{ right: -7 }}
        />
      )}
    </div>
  );
};

export default PreApprovedTemplateNode;
